import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCompleteCartMutation } from '../services/api';

/**
 * Handles the return from redirect-based payment methods (Klarna, Swish).
 *
 * After the customer completes payment on the provider's page, Stripe redirects
 * back to /checkout/confirm with query params:
 *   ?redirect_status=succeeded&payment_intent=pi_xxx&payment_intent_client_secret=pi_xxx_secret_yyy
 *
 * This component:
 * 1. Reads redirect_status from URL
 * 2. Retrieves cartId from sessionStorage (saved before redirect)
 * 3. Calls completeCart to finalize the order
 * 4. Navigates to /order-confirmation/:orderId
 */
const CheckoutConfirm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [completeCart] = useCompleteCartMutation();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React strict mode
    if (hasRun.current) return;
    hasRun.current = true;

    const handleRedirectReturn = async () => {
      const redirectStatus = searchParams.get('redirect_status');
      const cartId = sessionStorage.getItem('checkout_cart_id');

      if (!cartId) {
        setError('Checkout session expired. Please start a new checkout.');
        setIsProcessing(false);
        return;
      }

      if (redirectStatus !== 'succeeded') {
        sessionStorage.removeItem('checkout_cart_id');
        setError(
          redirectStatus === 'failed'
            ? 'Payment was declined. Please try again.'
            : `Payment was not completed (status: ${redirectStatus || 'unknown'}). Please try again.`
        );
        setIsProcessing(false);
        return;
      }

      try {
        // Payment succeeded — complete the cart to create an order
        const result = await completeCart({ cartId }).unwrap();

        sessionStorage.removeItem('checkout_cart_id');

        if (result.type === 'order' && result.order) {
          navigate(`/order-confirmation/${result.order.id}`, {
            state: { order: result.order },
            replace: true,
          });
        } else {
          setError('Order could not be finalized. Please contact support.');
          setIsProcessing(false);
        }
      } catch (err) {
        console.error('[CheckoutConfirm] Failed to complete cart:', err);
        sessionStorage.removeItem('checkout_cart_id');
        setError('Failed to finalize your order. Please contact support.');
        setIsProcessing(false);
      }
    };

    handleRedirectReturn();
  }, [searchParams, completeCart, navigate]);

  if (isProcessing) {
    return (
      <div className="checkout-container">
        <div className="loading">
          <h2>Completing your order...</h2>
          <p>Please wait while we finalize your payment.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-container">
        <div className="error">
          <h2>Payment Issue</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/checkout')} className="btn btn-primary">
            Return to Checkout
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default CheckoutConfirm;
